class ProductBreakdownContent extends HTMLElement {
	#onResize = null;

	connectedCallback() {
		this.content = this.querySelector('[data-pb-content]');
		this.button = this.querySelector('[data-show-more]');
		this.label = this.querySelector('[data-show-more-text]');
		if (!this.hasAttribute('data-collapsible') || !this.content || !this.button) {
			return;
		}
		const container = this.closest('.product-breakdown__container');
		this.mediaColumn = container?.querySelector('.product-breakdown__media-column') || null;
		this.button.addEventListener('click', () => this.#toggle());
		this.#evaluate();
		// Re-test on reflow (web-font swap, viewport/image-size change) since both column heights shift.
		this.#onResize = this.#debounce(() => this.#evaluate(), 150);
		window.addEventListener('resize', this.#onResize);
	}

	disconnectedCallback() {
		if (this.#onResize) {
			window.removeEventListener('resize', this.#onResize);
		}
	}

	// The collapse target: match the image column's height while the two columns sit side by side,
	// so they stay even. Single column (no image, or stacked on mobile) → the fallback height.
	#cap() {
		const fixed = parseFloat(this.dataset.collapsedHeight) || 0;
		if (!this.mediaColumn) {
			return fixed;
		}
		const sideBySide = Math.abs(this.mediaColumn.offsetTop - this.offsetTop) < 4;
		if (!sideBySide) {
			return fixed;
		}
		const mediaHeight = this.mediaColumn.offsetHeight;
		return mediaHeight > 0 ? mediaHeight : fixed;
	}

	// scrollHeight is the full natural height regardless of the max-height clamp, so it's a
	// stable overflow test in either state. No overflow → drop the clamp + hide the button.
	#evaluate() {
		const cap = this.#cap();
		this.style.setProperty('--pb-collapsed-height', `${cap}px`);
		const overflowing = this.content.scrollHeight > cap + 1;
		this.toggleAttribute('data-overflow', overflowing);
		if (!overflowing) {
			this.removeAttribute('data-expanded');
			this.button.setAttribute('aria-expanded', 'false');
			if (this.label) {
				this.label.textContent = this.button.dataset.labelMore || this.label.textContent;
			}
		}
	}

	#toggle() {
		const expanded = this.toggleAttribute('data-expanded');
		this.button.setAttribute('aria-expanded', String(expanded));
		if (this.label) {
			this.label.textContent = expanded ? this.button.dataset.labelLess : this.button.dataset.labelMore;
		}
	}

	#debounce(fn, wait) {
		let timer;
		return () => {
			clearTimeout(timer);
			timer = setTimeout(fn, wait);
		};
	}
}

if (!customElements.get('product-breakdown-content')) {
	customElements.define('product-breakdown-content', ProductBreakdownContent);
}
